import Banner from '@cpg-its/ads.containers.banner';
import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { Redirect } from 'react-router-dom';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { ArcRoutes } from '../../../../constants/routes';
import { LayDcPlanAdoptionContentModel, LayDcPlanFormModel } from '../models/plan-adoption-form-models';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';
import { QccoQuestionsShared } from '../shared/qcco-questions-shared';

interface Props {
    wizard: QccoPlanAdoptionWizard;
}

function QccoQuestionsForm(props: Props) {

    const { wizard } = props;
    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(content?.gpsType);
    const apiErrors = useApiResponseError();
    const currentInstitution = wizard?.wizardOptions?.currentInstitution;
    const initialValues: LayDcPlanFormModel = wizard?.getFormInitialValue();
    const validationSchema = wizard?.getFormValidationSchema();

    const institutionRootPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}`);
    const isIndemnificationComplete = wizard?.isIndemnificationComplete();
    const isIndemnificationRequired = wizard?.isIndemnificationRequired();




    function renderIdenmificationBanner() : any {
        if (!isIndemnificationRequired) return null;

        const indemnifySuccess = isIndemnificationComplete && initialValues?.complyEpiscopalChurchLayPensionSystem !== YesNoIndicators.No;

        return <ContentSpacer>
            <Banner
                banner={{
                    type: 'success',
                    label: <WysiwygWithReactLink content={indemnifySuccess ? content?.indemnifySuccessMessage : content?.indemnifySkippedMessage} />,
                }}
            />
        </ContentSpacer>;
    }

    if (!wizard) {
        return <Redirect to={institutionRootPath} />;
    }

    if (!isIndemnificationComplete) {
        return <Redirect to={wizard?.wizardOptions?.featureRootPath} />;
    }

    return (
        <PageContainer>
            <MainContent>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        const cleanedValues = { ...values };
                        if (values.qccoQuestion1 === YesNoIndicators.Yes) {
                            cleanedValues.qccoQuestion2 = YesNoIndicators.NotApplicable;
                            cleanedValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
                        } else if (values.qccoQuestion1 === YesNoIndicators.No && values.qccoQuestion2 === YesNoIndicators.No) {
                            cleanedValues.qccoQuestion3 = YesNoIndicators.NotApplicable;
                        }
                        
                        wizard?.onSubmit({
                            values: cleanedValues,
                            clientId: currentInstitution?.org_client_nbr,
                        });
                    }}
                    enableReinitialize
                    initialErrors={apiErrors}
                >
                    {(formProps: FormikProps<LayDcPlanFormModel>) => {
                        return (
                        <>
                            <Form>
                                <Grid>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel({
                                            values: formProps.values,
                                            clientId: currentInstitution?.org_client_nbr,
                                        })}
                                        submitDisabled={wizard?.isSubmitDisabled(formProps.values)}
                                        preventButtonDisableValidation={wizard?.preventButtonDisableValidation()}
                                        submitText={content?.submitLabel}
                                        cancelText={content?.cancelLabel}
                                    >
                                        {renderIdenmificationBanner()}
                                        <PageHeader
                                            headerText={content?.pageTitle}
                                            description={content?.PageDescription}
                                        />
                                        <Hidden mdUp>
                                            <SideBarContent>
                                                <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                            </SideBarContent>
                                        </Hidden>
                                        <QccoQuestionsShared
                                            formProps={formProps}
                                            content={content}
                                            showStatus
                                        />
                                    </FormWrapper>
                                </Grid>
                            </Form>
                        </>
                        );
                    }}
                </Formik>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );

}

export default QccoQuestionsForm;